/**
 * Node Modules
 */
import React from 'react'
import { Link } from 'gatsby'
import { Helmet } from 'react-helmet'
import { graphql } from 'gatsby'

/**
 * Application Modules
 */
import PageContainer from '../../components/page-container'
import Footer from '../../components/footer'
import Metadata from '../../components/metadata'
import Header from '../../components/header'
import Hero from '../../sections/community-guidelines/hero'
import DownloadBanner from '../../components/download-banner'
import Section from '../../components/section'
import Spacer from '../../components/spacer'

import './styles.css'

/**
 * Page Component
 */
export default function Page({data}) {
  const {
    referenceCommunityGuidelinesMetaData,
  } = data.contentfulSingletonStaticPageMetadata

  return (
    <PageContainer>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="canonical" href="https://kinzoo.com/community-guidelines/" />
        <meta property="og:url" content="https://kinzoo.com/community-guidelines/" />
      </Helmet>
      <Metadata {...referenceCommunityGuidelinesMetaData} />
      <Header
        styles={{
          '--logo-color': 'var(--purple)',
          '--color': 'var(--light-purple)',
          '--active-color': 'var(--coral)',
          '--background-color': 'var(--white)',
        }}
      />
      <Hero
        hed={`Community Guidelines`}
        dek={`Protecting children's safety is our number one priority, so we ask that everyone follow a few golden rules when using the app.`}
      />
      <Section className="page-guidelines" id="guidelines">
        <div className="page-guidelines__grid grid">
          <div className="column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
            <ol className="guidelines__list">
              <li className="guidelines__list-item yellow">
                <h3 className="guidelines__item-hed">Keep your personal information personal.</h3>
                <p className="guidelines__item-dek">
                  Some things—like your email, address, phone number, social insurance number,
                  passport, identification—shouldn't be shared on Kinzoo. We ask that you don't
                  exchange your personally identifiable information, or anyone else's in the app.
                </p>
              </li>
              <li className="guidelines__list-item green">
                <h3 className="guidelines__item-hed">Be yourself.</h3>
                <p className="guidelines__item-dek">
                  Tricking other users or pretending to be someone else, including Kinzoo employees,
                  other family members or users isn't allowed. We also ask users to be truthful
                  about their age when creating their accounts—because this helps us keep minors
                  safe.
                </p>
              </li>
              <li className="guidelines__list-item coral">
                <h3 className="guidelines__item-hed">Keep your account secure.</h3>
                <p className="guidelines__item-dek">
                  Your whole family's PINs and Temporary Access Keys should be kept secret and safe.
                  Make sure you go to the Profile Switch Screen before sharing Kinzoo on your
                  device. And, only one Kinzoo account per person—no duplicate accounts, please!
                </p>
              </li>
              <li className="guidelines__list-item blue">
                <h3 className="guidelines__item-hed">Keep content kid-friendly.</h3>
                <p className="guidelines__item-dek">
                  Kinzoo is meant to be a safe space for all ages, so don't share anything that's
                  not appropriate for children. That means no pornographic content, bad words or
                  external links. We also do not tolerate any content or language that's violent,
                  promotes self-harm or suicide.
                </p>
              </li>
              <li className="guidelines__list-item pink">
                <h3 className="guidelines__item-hed">Treat others with respect.</h3>
                <p className="guidelines__item-dek">
                  There is no room for threats, abuse, violence, harassment, bullying or harmful
                  behavior on Kinzoo. This is an inclusive community, and we do not tolerate hateful
                  or discriminatory behavior based on race, color, sex, religion, nationality,
                  ethnic or national origin, marital status, disability, sexual orientation or age.
                </p>
              </li>
              <li className="guidelines__list-item purple">
                <h3 className="guidelines__item-hed">Help us keep Kinzoo safe.</h3>
                <p className="guidelines__item-dek">
                  There are some things that only adults should do on Kinzoo, so if you're a kid,
                  ask an adult for help. And, if you see anything in Kinzoo that doesn't respect
                  these guidelines, use our in-app reporting feature or contact us right away.
                </p>
              </li>
            </ol>

          </div>
        </div>
      </Section>
      <Section className="page-guidelines__footer">
        <Spacer size={[100, 120]} />
        <div className=" grid">
          <div className="column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
            <p>
              If a user violates any of the community guidelines, Kinzoo may impose consequences,
              which vary depending on the severity of the situation. Everything is reviewed on a
              case-by-case basis. We consider the type of violation, as well as whether a user has a
              history of past violations. More severe cases may result in a temporary or permanent
              ban from Kinzoo and enforcement is at our sole discretion.
            </p>
            <p>
              Our Community Guidelines are subject to change and aren’t exhaustive. For a complete
              list of rules, check out our <Link to="/terms">Terms of Service</Link>, or{' '}
              <a href="mailto:hello@kinzoo.com">contact us</a> if you still have questions. We
              appreciate everyone's efforts in making Kinzoo a safe space!
            </p>
          </div>
        </div>
      </Section>
      <Spacer size={[50, 65]} />
      <DownloadBanner />
      <Footer />
    </PageContainer>
  )
}


export const pageQuery = graphql`
  query CommunityGuidelinesQuery {
    contentfulSingletonStaticPageMetadata(contentful_id: {eq: "37wd06meITbnwAFYKdxp70"}) {
      referenceCommunityGuidelinesMetaData {
        description
        keywords
        ogDescription
        ogTitle
        ogType
        pageTitle
        twitterCard
        twitterCreator
        twitterDescription
        twitterTitle
        ogImage {
          file {
            url
          }
        }
        twitterImage {
          file {
            url
          }
        }
      }
    }
  }
`
